/**
 * usePortfolioStatistics — TanStack Query hook for the Portfolio analytics page (Story UI-U7).
 *
 * live mode → GET /statistics/portfolio via the typed client (PortfolioStatisticsDto — no reshape).
 * mock mode → resolves MOCK_PORTFOLIO_STATISTICS synchronously (initialData; deterministic tests).
 *
 * Mirrors useHeuteRenewals (the V10-J mock-swap pattern). The Renewal-Pipeline is NOT
 * fetched here — the screen reuses the existing useHeuteRenewals hook for that.
 *
 * [Source: apps/web/src/features/dashboard/hooks/useHeuteRenewals.ts]
 * [Source: docs/stories/ui-u7-portfolio.md Task 5]
 */

import { useQuery } from '@tanstack/react-query';
import type { PortfolioStatisticsDto } from '@brokly/shared';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { apiClient } from '../../../lib/api-client';
import { MOCK_PORTFOLIO_STATISTICS } from '../mock';

export function usePortfolioStatistics() {
  const live = isLiveData();
  return useQuery<PortfolioStatisticsDto>({
    queryKey: qk.portfolioStatistics(),
    queryFn: () =>
      live ? apiClient.statistics.portfolio() : Promise.resolve(MOCK_PORTFOLIO_STATISTICS),
    // mock mode: synchronous initialData (no loading flicker, deterministic tests)
    ...(live ? {} : { initialData: MOCK_PORTFOLIO_STATISTICS, staleTime: Infinity }),
  });
}
